import {
  json,
  redirect,
} from "@remix-run/cloudflare";
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/cloudflare";
import {
  Form,
  useActionData,
  useLoaderData,
  useNavigation,
} from "@remix-run/react";
import { parseWithValibot as conformParse } from "conform-to-valibot";
import clsx from "clsx";
import { UserService } from "~/database/user.service";
import { createSbClientSSR } from "~/utils/createSbClientSSR";
import Stripe from "stripe";
import { StripeService } from "~/database/stripe.service";
import { environment } from "~/utils/env";
import { useState } from "react";
import {
  buttonBase,
  inputBase,
} from "~/components/Common/FormStyle";
import {
  getFormProps,
  getInputProps,
  useForm,
} from "@conform-to/react";
import {
  minLength,
  object,
  optional,
  parse,
  string,
} from "valibot";

import { NOT_ALOOW_INPUT } from "~/utils/authUtils";
import ErrorDisplay from "~/components/Common/ErrorDisplay";
import {
  DEFAULT_DESCRIPTION,
  TITLE_SUFFIX,
} from "~/utils/const";

export const meta: MetaFunction = () => {
  return [
    { title: "プラン選択" + TITLE_SUFFIX },
    {
      name: "description",
      content: DEFAULT_DESCRIPTION,
    },
  ];
};

export async function loader({
  request,
  context,
}: LoaderFunctionArgs) {
  try {
    const headers = new Headers();
    const supabase = createSbClientSSR(
      headers,
      request,
      context,
    );

    const userResponse =
      await supabase.auth.getUser();
    const userId = userResponse?.data?.user?.id;
    console.log("userId:", userId);
    if (!userId) {
      return redirect("/login");
    }

    const user = await UserService.getUser(
      supabase,
      userId,
    );
    return json(
      {
        user: user,
      },
      {
        headers: headers,
      },
    );
  } catch (e) {
    return redirect("/");
  }
}

export const pricePlans = [
  {
    id: "npo",
    // name: "非営利団体",
    name: "NON-PROFIT",
    desc: "NPOやNGOといった<br/>社会的な活動をされている団体",
    price: 0,
    priceIdLocal:
      "price_1SAWvTFBqCcRcpFEyH4KnfLK",
    priceId: "price_1Q8MtNF0oXOuwiRI38OH1bI8",
    priceIdPrd: "price_1S42xOF0oXOuwiRImDizl6ES",
  },
  /* {
    id: "free",
    name: "個人事業主",
    desc: "フリーランスや個人商店の方など",
    price: 500,
    priceIdLocal: "price_1P6VgrF0oXOuwiRIgqKMAuGH",
    priceId: "price_1P6VgrF0oXOuwiRIgqKMAuGH",
  }, */
  {
    id: "smallCom",
    //name: "中小企業",
    name: "SMALL COMPANY",
    desc: "フリーランスや個人商店といった<br/>個人レベルで活動している方",
    price: 550,
    priceIdLocal:
      "price_1SAWv0FBqCcRcpFENr71pZr1",
    priceId: "price_1QidsFF0oXOuwiRIyGi3IIcL",
    priceIdPrd: "price_1S42yLF0oXOuwiRIAlUr5QlB",
  },
  {
    id: "com",
    //name: "大企業",
    name: "BIG COMPANY",
    desc: "上場企業および準ずる規模の法人を対象<br/>資本金1億円以上/従業員300名以上",
    price: 5000,
    priceIdLocal:
      "price_1SAWvpFBqCcRcpFEhsCDg15x",
    priceId: "price_1P6VhVF0oXOuwiRI1RwqKM2T",
    priceIdPrd: "price_1S42wqF0oXOuwiRIqUZ3mtwu",
  },
];

export async function action({
  request,
  context,
}: ActionFunctionArgs) {
  const env = environment(context);
  const stripe = new Stripe(
    env.STRIPE_SECRET_KEY,
  );
  const headers = new Headers();
  const supabase = createSbClientSSR(
    headers,
    request,
    context,
  );

  const formData = await request.formData();
  const dataFields = Object.fromEntries(
    formData.entries(),
  );
  const submission = conformParse(formData, {
    schema: UserProfileEdit,
  });
  if (submission.status !== "success") {
    return json({
      success: false,
      type: "error",
      error: {
        auth: NOT_ALOOW_INPUT,
        ...submission.error,
      },
      submission,
    });
  }

  const userResponse =
    await supabase.auth.getUser();
  const userId = userResponse?.data?.user?.id;

  if (!userId) {
    return redirect("/login");
  }

  const parsed = parse(
    UserProfileEdit,
    dataFields,
  );
  await UserService.updateUser(supabase, userId, {
    name: parsed.name,
  });
  if (dataFields.planId !== "nposss") {
    const selected = pricePlans.filter(
      (el) => el.id === parsed.planId,
    );
    if (
      userResponse?.data?.user?.email &&
      userId
    ) {
      const user =
        await UserService.getUserMine(supabase);
      const { clientSecret, status, reason } =
        await StripeService.createSubscription(
          stripe,
          {
            priceId:
              env.DB_ENV === "local"
                ? selected[0].priceIdLocal
                : env.DB_ENV === "prd"
                  ? selected[0].priceIdPrd
                  : selected[0].priceId,
            customerId: user.customer_id,
            code: parsed.promotionCode,
          },
        );
      if (
        reason === "invalid_promo_code" &&
        parsed.promotionCode
      ) {
        return json({
          success: false,
          type: "error",
          error: {
            auth: "Invalid promo code",
          },
          submission,
        });
      }
      // NPO
      if (status !== "canceled") {
        const supabaseAdmin = createSbClientSSR(
          headers,
          request,
          context,
          true,
        );
        await UserService.updatePermission(
          supabaseAdmin,
          userId,
          {
            subscription_active: true,
          },
        );
      }
      if (clientSecret) {
        return redirect(
          `/card?secret=${clientSecret}`,
        );
      } else {
        return redirect(
          `/user/${userId}?complete=1`,
        );
      }
    }
  } else {
    /* NPO */
    /* subscription_status をアクティブにする */
    const supabaseAdmin = createSbClientSSR(
      headers,
      request,
      context,
      true,
    );
    await UserService.updatePermission(
      supabaseAdmin,
      userId,
      {
        subscription_active: true,
      },
    );
    return redirect(`/user/${userId}?complete=1`);
  }
}

export const UserProfileEdit = object({
  name: string("coompany name required", [
    minLength(1, "coompany name required"),
  ]),
  planId: string("select plan required", [
    minLength(1, "select plan required"),
  ]),
  promotionCode: optional(string()),
});

export default function Index() {
  const [select, setSelect] = useState("");
  const { user } = useLoaderData<typeof loader>();

  const [form, { name, planId, promotionCode }] =
    useForm({
      onValidate({ formData }) {
        return conformParse(formData, {
          schema: UserProfileEdit,
        });
      },
      defaultValue: {
        name: user.name,
      },
    });
  console.log("name:", name);

  const data = useActionData<typeof action>();
  const navigation = useNavigation();
  return (
    <div
      className={clsx(
        "pb-128 pt-72",

        navigation.state === "loading" ||
          (navigation.state === "submitting" &&
            "opacity-70"),
        navigation.state === "loading" ||
          (navigation.state === "submitting" &&
            "pointer-events-none"),
      )}
    >
      <div className={clsx("text-center")}>
        <ErrorDisplay
          error={data?.error}
        ></ErrorDisplay>
      </div>
      <div
        className={clsx(
          "max-w-screen-lg",
          "mx-auto",
        )}
      >
        <Form
          method="post"
          {...getFormProps(form)}
        >
          <h2
            className={clsx(
              "text-32",
              "text-center",
            )}
          >
            プランをお選びください
          </h2>
          <div className={clsx("mt-48")}>
            <div
              className={clsx(
                "text-red-800",
                "pb-20",
                "text-center",
              )}
            >
              <ErrorDisplay
                error={
                  planId.errors && {
                    input: planId.errors,
                  }
                }
              ></ErrorDisplay>
            </div>
            <input
              {...getInputProps(planId, {
                type: "hidden",
              })}
              value={select}
            />
            <div
              className={clsx(
                "grid",
                "grid-cols-3",
                "gap-32",
              )}
            >
              {pricePlans.map((el, i) => {
                return (
                  <div
                    key={i}
                    className={clsx(
                      "border",
                      "border-black",
                      "text-center",
                      "py-20",
                      "cursor-pointer",
                      el.id === select &&
                        "bg-amber-200",
                    )}
                    onClick={() => {
                      setSelect(el.id);
                    }}
                  >
                    <h3
                      className={clsx(
                        "text-24",
                        "font-bold",
                        "font-en",
                      )}
                    >
                      {el.name}
                    </h3>
                    <div
                      className={clsx(
                        "text-12",
                        "text-gray-400",
                        "min-h-32",
                        "mt-8",
                      )}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: el.desc,
                        }}
                      />
                    </div>
                    <div
                      className={clsx(
                        "mt-8",
                        "font-bold",
                      )}
                    >
                      {el.price === 0 ? (
                        <div
                          className={clsx(
                            "pt-16",
                            "text-28",
                          )}
                        >
                          無料
                        </div>
                      ) : (
                        <div>
                          <div
                            className={clsx(
                              "text-sm",
                            )}
                          >
                            月額
                          </div>
                          <div
                            className={clsx(
                              "text-28",
                            )}
                          >
                            ¥{el.price}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div
            className={clsx(
              "flex",
              "justify-center",
              "gap-16",
              "mt-44",
            )}
          >
            <div
              className={clsx("pt-4", "text-lg")}
            >
              法人名
            </div>
            <div className={clsx("w-400")}>
              <input
                className={inputBase()}
                placeholder="株式会社OURPR"
                {...getInputProps(name, {
                  type: "text",
                })}
              />
              <div
                className={clsx(
                  "text-12",
                  "mt-2",
                  "pl-8",
                )}
              >
                <div
                  className={clsx(
                    "text-red-800",
                    "pb-8",
                  )}
                >
                  <ErrorDisplay
                    error={
                      name.errors && {
                        input: name.errors,
                      }
                    }
                  ></ErrorDisplay>
                </div>

                <div>
                  個人事業主の方は屋号またはサービス名をご入力ください
                </div>
              </div>
            </div>
          </div>
          <div
            className={clsx(
              "flex",
              "justify-center",
              "gap-16",
              "mt-44",
            )}
          >
            <div
              className={clsx("pt-4", "text-lg")}
            >
              プロモーションコード
            </div>
            <div className={clsx("w-400")}>
              <input
                className={inputBase()}
                placeholder="プロモーションコード"
                {...getInputProps(promotionCode, {
                  type: "text",
                })}
              />
              <div
                className={clsx(
                  "text-12",
                  "mt-2",
                  "pl-8",
                )}
              >
                <div
                  className={clsx(
                    "text-red-800",
                    "pb-8",
                  )}
                >
                  <ErrorDisplay
                    error={
                      promotionCode.errors && {
                        input:
                          promotionCode.errors,
                      }
                    }
                  ></ErrorDisplay>
                </div>
              </div>
            </div>
          </div>
          <div
            className={clsx(
              "text-12",
              "text-gray-400",
              "w-[90%]",
              "max-w-xl",
              "mx-auto",
              "mt-40",
            )}
          >
            OURPRは利用者の属性により、価格が変わる「ソーシャル・プライシング」を採用しています。まず、NPOやNGOといった社会的な活動をされている団体は無料です。フリーや個人で活動される方、もしくや個人商店の方などは500円。資金に余裕のある大企業は5000円といったように、それぞれの活動領域やフェーズによって価格を変えるという新しい取り組みです。
          </div>
          <div
            className={clsx(
              "text-center",
              "mt-48",
            )}
          >
            <button
              className={buttonBase()}
              type="submit"
            >
              次へ
            </button>
          </div>
        </Form>
      </div>
    </div>
  );
}
